/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { OfferStatus } from 'types/enums/OfferStatus'

export type OfferStatusValue = `${OfferStatus}`

export interface OfferStatusDescriptorKeys {
    value: OfferStatus
    labelKey: string
    descriptionKey?: string
}

export interface OfferStatusDescriptor {
    value: OfferStatus
    label: string
    description?: string
}

export const getOfferStatusDescriptorsKeys =
    (): OfferStatusDescriptorKeys[] => [
        {
            value: OfferStatus.Draft,
            labelKey: 'Draft',
        },
        {
            value: OfferStatus.New,
            labelKey: 'New',
        },
        {
            value: OfferStatus.Sent,
            labelKey: 'Sent',
        },
        {
            value: OfferStatus.Accepted,
            labelKey: 'Accepted',
        },
        {
            value: OfferStatus.Reconfirm,
            labelKey: 'Reconfirm',
        },
		{
            value: OfferStatus.Finished,
            labelKey: 'Completed',
        },
        {
            value: OfferStatus.Rejected,
            labelKey: 'Rejected',
        },
        {
            value: OfferStatus.Expired,
            labelKey: 'Expired',
        },
    ]

export const getOfferStatusDescriptors = (
    t: (key: string, options?: any) => string
): OfferStatusDescriptor[] =>
    getOfferStatusDescriptorsKeys().map((d) => ({
        value: d.value,
        label: t(d.labelKey, { defaultValue: d.labelKey }),
        description: d.descriptionKey
            ? t(d.descriptionKey, { defaultValue: d.descriptionKey })
            : undefined,
    }))

export const getOfferStatusMap = (
    t: (key: string, options?: any) => string
): Record<OfferStatus, OfferStatusDescriptor> => {
    const list = getOfferStatusDescriptors(t)
    const map = {} as Record<OfferStatus, OfferStatusDescriptor>
    list.forEach((d) => {
        map[d.value] = d
    })
    return map
}

export const getOfferStatusOptions = (
    t: (key: string, options?: any) => string
): { label: string; description?: string; value: string }[] =>
    getOfferStatusDescriptors(t).map((d) => ({
        label: d.label,
        description: d.description,
        value: d.value,
    }))

export const getOfferStatusByValue = (
    value: OfferStatus | OfferStatusValue,
    t: (key: string, options?: any) => string
): OfferStatusDescriptor => {
    const map = getOfferStatusMap(t)
    const v = (value as OfferStatus) ?? OfferStatus.Draft
    return map[v]
}
